<template>
    <div>
        <form id="smmr-form" @submit.prevent="update">
            <div class="p-8 flex flex-wrap text-left" @change="dirtyForm()" @keydown="dirtyForm">
                <div class="mb-4">
                    Please make your selection of programme and mode of study from the drop-down menus below. 
                    <span>You can make a maximum of {{ choiceLimit }} programme choices. </span>
                    <span v-if="choiceLimit > 1">Your choices will be considered in sequence starting with your first choice. If you are offered a place on your first choice programme you
                        will not be considered for any of your subsequent programme choices, unless you withdraw your first choice application. </span>
                    <span>If you are applying for multiple {{ selectedProgrammeType?.name }}, you will need to start a new {{ selectedProgrammeType?.name }} application for each.</span>
                </div>

                <label class="font-bold mb-2 w-full">
                    Please select the type of programme from the drop-down menu below:
                </label>

                <div class="flex flex-row">
                    <select-input id="programmeTypeSelect" v-model="programmeType" class="pr-6" :disabled="!!programmeType">
                        <option v-for="pt in programmeTypesList.data" :key="pt" :value="pt.sits_code">
                            {{ pt.name }}
                        </option>
                    </select-input>
                    <span v-if="!!programmeType" class="pl-2 text-sm">You already selected a programme type.</span>
                </div>
                <div class="w-full">
                    <div v-for="(programme) in myProgrammes" :key="programme.reference">
                        <h1 class="text-1xl py-2 text-green-600 font-bold">Programme choice {{ programme.choice }} <span v-if="programme.activeChoice">(Active Choice)</span></h1>
                        <table class="mb-8">
                            <tbody>
                                <tr>
                                    <th>Programme Type:</th>
                                    <td>{{ programme.programme_type }}</td>
                                </tr>
                                <tr>
                                    <th>Mode of study and start date:</th>
                                    <td>{{ programme.programmeInstance.data.moa_name }} ({{ programme.programmeInstance.data.moa_start_date }})</td>
                                </tr>
                                <tr>
                                    <th>Programme title:</th>
                                    <td>{{ programme.programmeInstance.data.programme_title }}</td>
                                </tr>
                                <tr v-if="programme.proposed_start_date">
                                    <th>Proposed start date:</th>
                                    <td>{{ programme.proposed_start_date }}</td>
                                </tr>
                                <tr v-if="programme.msc_dl_course !== null">
                                    <th>DL MSc Course:</th>
                                    <td>{{ programme.msc_dl_course }}</td>
                                </tr>
                                <tr v-if="programme.student_id !== null">
                                    <th>Student ID:</th>
                                    <td>{{ programme.student_id }}</td>
                                </tr>
                                <tr v-if="programme.other_current_modules !== null">
                                    <th>Other Module:</th>
                                    <td>
                                        <table class="min-w-full divide-y divide-200 border-collapse">
                                            <thead class="bg-50">
                                                <tr>
                                                    <th class="px-3 border border-300 text-left text-xs font-medium text-500 uppercase tracking-wider">
                                                        Count
                                                    </th>
                                                    <th class="px-3 border border-300 text-left text-xs font-medium text-500 uppercase tracking-wider">
                                                        Module Name and Code
                                                    </th>
                                                    <th class="px-3 border border-300 text-left text-xs font-medium text-500 uppercase tracking-wider">
                                                        Grade
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white divide-y divide-200">
                                                <tr v-for="(module, index) in programme.other_current_modules" :key="module.id" class="border-b border-300">
                                                    <td class="px-3 whitespace-nowrap text-sm text-900 border border-300">
                                                        {{ index + 1 }}
                                                    </td>
                                                    <td class="px-3 whitespace-nowrap text-sm text-500 border border-300">
                                                        {{ module.moduleName }}
                                                    </td>
                                                    <td class="px-3 whitespace-nowrap text-sm text-500 border border-300">
                                                        {{ module.grade }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr v-if="programme.is_alumni !== null">
                                    <th>Are you LSHTM alumni:</th>
                                    <td>{{ programme.is_alumni ? 'Yes' : 'No' }}</td>
                                </tr>
                                <tr v-if="programme.alumni_course_info !== null">
                                    <th>Alumni course studied and year of graduation:</th>
                                    <td>{{ programme.alumni_course_info }}</td>
                                </tr>
                                <tr>
                                    <div class="flex">
                                        <button v-if="!programme.activeChoice" type="button" class="bg-yellow-600 hover:bg-yellow-700 text-white py-1 px-2 rounded text-sm" @click="makeActiveChoice(programme.reference)">
                                            Make active choice
                                        </button>
                                        <button type="button" class="bg-red-600 hover:bg-red-700 text-white py-1 px-2 rounded text-sm ml-2" @click="removeProgrammeChoice(programme.reference)">
                                            Remove this choice
                                        </button>
                                    </div>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <button v-if="!showProgrammeForm && hasProgrammeAllowance" type="button" class="w-full lg:w-auto text-sm flex justify-center items-center bg-green-600 hover:bg-green-700 text-white lg:mb-8 py-2.5 px-4 rounded text-center" @click="addProgrammeChoice">
                        <icon name="plus-circle" class="w-6 h-6 mr-2" />
                        Add another choice
                    </button>
                </div>

                <div v-if="showProgrammeForm && hasProgrammeAllowance" class="w-full">
                    <div class="w-full mb-4">
                        Please select the programme(s) you are applying for from the drop-down menus below.
                    </div>
                    <ProgrammeSelectionForm :chosen-programme-type="chosenProgrammeType" :programmes-list="programmesList" :courses-list="coursesList" :mode-of-attendance="modeOfAttendance" :academic-year="applicationData?.data?.academicYear" :proposed-start-dates="proposedStartDates" />
                    <div v-if="successMessage" class="success-message w-auto">{{ successMessage }}</div>
                    <div v-if="serverError" class="w-auto server-error">{{ serverError }}</div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import SelectInput from '@/shared/SelectInput'
import Icon from '@/shared/Icon'
import ProgrammeSelectionForm from '@/shared/forms/ProgrammeSelectionForm'

export default {
    components: {
        ProgrammeSelectionForm,
        SelectInput,
        Icon,
    },
    props: {
        programmeData: {
            type: Object,
            default: function () {
                return {
                    programmeInstance: null,
                }
            },
        },
        programmeTypesList: Object,
        courses: Array,
        programmes: Object,
        programmeType: String,
        choice: Number,
        choiceTotal: Number,
        applicationRef: String,
        applicationData: Object,
        modeOfAttendance: Object,
        proposedStartDates: Object,
    },
    data() {
        return {
            myProgrammes: Object,
            programmesList: Object,
            coursesList: Array,
            formDirty: false,
            loading: false,
            successMessage: '',
            serverError: '',
            chosenProgrammes: this.applicationData?.data?.applicationProgrammes?.data ?? [],
            chosenProgrammeType: this.programmeType ?? this.applicationData['data']['first_choice_type'],
            firstChoice: this.applicationData['data']['first_choice'],
            secondChoice: this.applicationData['data']['second_choice'],
            firstChoiceMode: this.applicationData['data']['first_choice_mode'],
            secondChoiceMode: this.applicationData['data']['second_choice_mode'],
            proposedStartDate: this.applicationData['data']['proposed_start_date'],
            proposedResearchTopic: this.applicationData['data']['proposed_research_topic'],
            proposedSupervisor: this.applicationData['data']['proposed_supervisor'],
            mrcStaff: this.applicationData['data']['mrc_staff'],
            selectedProgrammes: [],
            programmeNames: [],
            showSecondChoice: false,
            showProgrammeForm: false,
            secondChoiceWarning: null,
            choiceLimit: 0,
            proposedStartDateShow: false,
        }
    },
    computed: {
        maxChoices: {
            get: function () {
                return this.choiceLimit
            },
            set: function (newValue) {
                this.choiceLimit = newValue
            },
        },
        hasProgrammeAllowance() {
            return this.myProgrammes.length < this.choiceLimit
        },
        choicesMatch() {
            return this.firstChoice === null || this.firstChoice === this.secondChoice
        },
        secondChoiceMade() {
            return this.secondChoice === null && this.showSecondChoice === true
        },
        programmeCount() {
            return this.myProgrammes.length ?? 0
        },
        programmeTypeChoice: {
            get: function () {
                if (this.applicationData['data']['first_choice'] !== '') {
                    return this.applicationData['data']['first_choice']
                } else {
                    return null
                }
            },
        },
        selectedProgrammeType() {
            return this.programmeTypesList.data.find(p => p.sits_code === this.programmeType)
        },
    },
    mounted() {
        this.proposedStartDateShow = this.chosenProgrammeType === 'R'
        this.choiceLimit = this.applicationData['data']['max_choices']
        this.myProgrammes = this.programmeData
        for (const i in this.programmes) {
            this.programmeNames[this.programmes[i]['reference']] = this.programmes[i]
        }
        if(this.myProgrammes.length <= 0) {
          this.showProgrammeForm = true
        }

        const filters = [
            {type: 'programme_type_sits_code', name: this.chosenProgrammeType},
            {type: 'sits_ipo_moac', name: this.firstChoiceMode},
        ]
        this.programmesList = this.programmes
        this.coursesList = this.courses

        const filters2 = [
            {type: 'programme_type_sits_code', name: this.chosenProgrammeType},
            {type: 'sits_ipo_moac', name: this.secondChoiceMode},
        ]
        this.programmesList2 = this.programmes.filter(el => filters2.every(filterEl => el[filterEl.type] === filterEl.name))

        if (this.secondChoice !== null) {
            this.showSecondChoice = true
        }
        this.programmesList.sort((a,b) => (a.programme_title > b.programme_title) ? 1 : ((b.programme_title > a.programme_title) ? -1 : 0))
    },
    methods: {
        addProgrammeChoice() {
            this.showProgrammeForm = true
        },
        removeProgrammeChoice(programmeRef) {
          this.$store.dispatch('deleteProgrammeDetails', {
            applicationProgrammeRef: programmeRef,
            applicationRef: this.applicationRef,
          })
              .then(response => {
                this.loading = false
                this.serverError = ''
                this.successMessage = response.data
                this.$emit('refreshApplication', true)
                window.location.reload()
              })
              .catch(error => {
                this.loading = false
                this.serverError = error.response.data
                this.successMessage = ''
              })
          this.formDirty = false
        },
      makeActiveChoice(programmeRef) {
        this.$store.dispatch('makeActiveChoice', {
          applicationProgrammeRef: programmeRef,
          applicationRef: this.applicationRef,
        })
            .then(response => {
              this.loading = false
              this.serverError = ''
              this.successMessage = response.data
              this.$emit('refreshApplication', true)
              window.location.reload()
            })
            .catch(error => {
              this.loading = false
              this.serverError = error.response.data
              this.successMessage = ''
            })
        this.formDirty = false
      },
        update() {
            this.myProgrammes[0] = this.firstChoice
            if (this.secondChoice !== null) {
                this.myProgrammes[1] = this.secondChoice
            }
            this.loading = true
            this.$store.dispatch('updateProgrammeDetails', {
                selectedProgrammes: this.myProgrammes,
                proposedStartDate: this.proposedStartDate,
                proposedResearchTopic: this.proposedResearchTopic,
                proposedSupervisor: this.proposedSupervisor,
                mrcStaff: this.mrcStaff,
                applicationRef: this.applicationRef,
            })
                .then(response => {
                    this.loading = false
                    this.serverError = ''
                    this.successMessage = response.data

                })
                .catch(error => {
                    this.loading = false
                    this.serverError = error.response.data
                    this.successMessage = ''
                })
            this.formDirty = false
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
    },
}
</script>